import { yupResolver } from '@hookform/resolvers/yup';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import React from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';

import { ContactInfoDto, ContactInfoTypeDto } from '@/api/generated';
import { FormProviderRc } from '@/components/form/FormProviderRC.comp.tsx';
import { SelectSearchOptionModel } from '@/components/form/model/SelectSearchOption.model.ts';
import { PhoneFormField } from '@/components/form/PhoneFormField.comp.tsx';
import { SelectSearchFormField } from '@/components/form/SelectSearchFormField.comp.tsx';
import { TextFormField } from '@/components/form/TextFormField.comp.tsx';
import { useUserContext } from '@/contexts/userContext/User.context.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';
import { AddContactInfoFormModel } from '@/views/contactsDetails/components/header/components/addContactInfoButton/model/AddContactInfoForm.model.ts';
import { useAddCustomerContactInfo } from '@/views/contactsDetails/components/header/components/addContactInfoButton/util/useAddContactInfo.util.ts';
import { useAddContactInfoFormValidation } from '@/views/contactsDetails/components/header/components/addContactInfoButton/util/useAddContactInfoForm.validation.ts';
import { useUpdateCustomerContactInfo } from '@/views/contactsDetails/components/header/components/addContactInfoButton/util/useUpdateContactInfo.util.ts';
import { useContactDetailsWrapperContext } from '@/views/contactsDetails/util/useContactDetailsWrapperContext.util.ts';

interface Props {
  item?: ContactInfoDto;
  onClose: () => void;
}

export const AddCustomerContactInfoDialog: React.FC<Props> = props => {
  const { item, onClose: handleClose } = props;
  const { t } = useTranslationWithPrefix('views.contactsDetails.infos.dialog');
  const { addCustomerContactInfo, isAdding } = useAddCustomerContactInfo();
  const { isUpdating, updateCustomerContactInfo } = useUpdateCustomerContactInfo();
  const validation = useAddContactInfoFormValidation();
  const { userId } = useUserContext();
  const {
    contextObject: {
      basicContact: { contactId },
    },
  } = useContactDetailsWrapperContext();

  const contactTypeOptions = Object.keys(ContactInfoTypeDto).map<
    SelectSearchOptionModel<ContactInfoTypeDto>
  >(it => {
    const key = ContactInfoTypeDto[it as keyof typeof ContactInfoTypeDto];
    return {
      label: t(`form.contactInfoTypeOptions.${key}`),
      value: key,
    };
  });

  const formMethods = useForm<AddContactInfoFormModel>({
    defaultValues: item
      ? {
          ...item,
          contactInfoType: contactTypeOptions.find(it => it.value === item.contactInfoType),
        }
      : {
          contactInfoType: null,
          value: '',
        },
    resolver: yupResolver(validation),
  });

  const handleSubmit: SubmitHandler<AddContactInfoFormModel> = async data => {
    const { contactInfoType } = data;
    if (!contactInfoType) {
      return;
    }

    const dto = {
      ...data,
      contactInfoType: contactInfoType.value,
      note: data.note ?? undefined,
    };
    if (item) {
      await updateCustomerContactInfo({
        contactId,
        contactInfoId: item.contactInfoId,
        dto,
        userId,
      });
    } else {
      await addCustomerContactInfo({
        contactId,
        dto,
        userId,
      });
    }
    handleClose();
  };

  const isDisabled = isAdding || isUpdating;
  const contactInfoType = formMethods.watch('contactInfoType');

  return (
    <Box sx={{ position: 'absolute' }}>
      <FormProviderRc {...formMethods} validation={validation}>
        <form onSubmit={formMethods.handleSubmit(handleSubmit)}>
          <Dialog disablePortal onClose={handleClose} open={true}>
            <DialogTitle>{t('title')}</DialogTitle>
            <DialogContent>
              <Stack spacing={2}>
                <SelectSearchFormField
                  disabled={isDisabled}
                  label={t('form.contactInfoType')}
                  name="contactInfoType"
                  options={contactTypeOptions}
                />
                {contactInfoType?.value === ContactInfoTypeDto.Phone ? (
                  <PhoneFormField disabled={isDisabled} label={t('form.value')} name="value" />
                ) : (
                  <TextFormField disabled={isDisabled} label={t('form.value')} name="value" />
                )}
                <TextFormField
                  disabled={isDisabled}
                  label={t('form.note')}
                  minRows={3}
                  multiline
                  name="note"
                />
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClose} variant="outlined">
                {t('button.cancel')}
              </Button>
              <Button type="submit">{t('button.submit')}</Button>
            </DialogActions>
          </Dialog>
        </form>
      </FormProviderRc>
    </Box>
  );
};
